import { ErrorInfo } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import './index.css';
import { store } from './redux/store';
import reportWebVitals from './reportWebVitals';
import ReactGA from 'react-ga4';
import Fallback from './pages/Fallback';
import { ErrorBoundary } from 'react-error-boundary';
import { handleChunkError } from './utils/functions';
import { PostHogProvider, posthog } from './utils/Posthog';
import { dispatchManagerFeatures } from './featureFlags/dispatchManagerFeatures';
import { clearExpiredSession } from './services/auth';

const { isPosthogOn, isBlankScreenFixEnabled } = dispatchManagerFeatures;

if (isBlankScreenFixEnabled) {
    clearExpiredSession();
}

const container = document.getElementById('root');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);

// Suppress specific React-Redux selector warnings in development until fixed
if (process.env.NODE_ENV === 'development') {
    const originalWarn = console.warn;
    console.warn = (...args) => {
        const message = args[0];
        if (
            typeof message === 'string' &&
            message.includes('Selector unknown returned a different result')
        ) {
            return; // Suppress this specific warning
        }
        originalWarn.apply(console, args);
    };
}

ReactGA.initialize('G-YV9TCM83JH');

window.onerror = function (message) {
    handleChunkError(message);
};

window.onunhandledrejection = function (event: PromiseRejectionEvent) {
    handleChunkError(event?.reason?.message);
};

navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations?.forEach(reg => reg?.unregister());
});

// Errors caught here never reach window.onerror, so PostHog only sees them if
// they are reported.
const reportRenderError = (error: Error, info: ErrorInfo): void => {
    if (isPosthogOn) {
        posthog.captureException(error, {
            componentStack: info.componentStack,
        });
    }
};

root.render(
    <ErrorBoundary
        FallbackComponent={Fallback}
        onError={isBlankScreenFixEnabled ? reportRenderError : undefined}
    >
        {isPosthogOn ? (
            <PostHogProvider client={posthog}>
                <Provider store={store}>
                    <BrowserRouter>
                        <App />
                    </BrowserRouter>
                </Provider>
            </PostHogProvider>
        ) : (
            <Provider store={store}>
                <BrowserRouter>
                    <App />
                </BrowserRouter>
            </Provider>
        )}
    </ErrorBoundary>,
);

const SendAnalytics = (): void => {
    ReactGA.send({
        hitType: 'pageview',
        page: window.location.pathname,
    });
};

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(SendAnalytics);
